/**
 * Must stay a superset-free MIRROR of Rust's `ITEM_TYPES` (src-tauri/src/vault.rs).
 * This list gates IMPORT only — export writes whatever type the vault holds — so
 * a type missing here is a backup that restores with items silently dropped.
 * `vaultTypeRegistryContract.test.js` pins it against both realms.
 */
export const VAULT_TYPES = ['password', 'sftp', 'dbConnection', 'authHeader', 'note', 'custom'];

export const VALID_TYPE_SET = new Set(VAULT_TYPES);

/**
 * Backend name bound: vault_put_item rejects any trimmed name that is empty or
 * longer than 200 chars ("name must be 1-200 characters"). Parsers clamp to
 * this so an over-long source name can't reject an otherwise-valid row.
 */
export const MAX_NAME_LEN = 200;

/* ───────────────────────────── small helpers ───────────────────────────── */

/** Coerce anything to a trimmed-safe string (null/undefined → ''). */
export function str(v) {
  if (v == null) return '';
  return typeof v === 'string' ? v : String(v);
}

/**
 * Produce a backend-safe display name: trim, fall back to `fallback` (then a
 * generic default) when empty, and clamp to MAX_NAME_LEN so the Rust
 * name-length validation can't reject an otherwise-valid item.
 */
export function clampName(name, fallback = 'Untitled') {
  const base = str(name).trim() || str(fallback).trim() || 'Untitled';
  return base.length > MAX_NAME_LEN ? base.slice(0, MAX_NAME_LEN) : base;
}

/** Join non-empty text blocks with blank-line-safe newlines. */
export function joinLines(parts) {
  return parts
    .map((p) => str(p).trim())
    .filter((p) => p !== '')
    .join('\n');
}

/**
 * Lenient host extraction (mirrors the Rust derive_index leniency): accepts a
 * full URL or a bare host, returns the lowercased hostname or null. Used only
 * for dedupe keys — the Rust side re-derives the stored index.urlHost.
 */
export function hostFromUrl(url) {
  const s = str(url).trim();
  if (!s) return null;
  const withScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(s) ? s : `http://${s}`;
  try {
    const u = new URL(withScheme);
    return u.hostname ? u.hostname.toLowerCase() : null;
  } catch {
    return null;
  }
}

/**
 * Dedupe key for "same name + username + host". Works off a normalized item
 * ({ name, data:{ username, url } }). NUL-separated so distinct field
 * boundaries can't collide.
 */
export function dedupeKey(item) {
  const d = (item && item.data) || {};
  const name = str(item && item.name).trim().toLowerCase();
  const username = str(d.username).trim().toLowerCase();
  const host = hostFromUrl(d.url) || '';
  return `${name}\u0000${username}\u0000${host}`;
}

/** Truthy CSV flag: "1" / "true" / "yes" / "y" (case-insensitive). */
/**
 * Stamp an imported item into a personal-vault FOLDER.
 *
 * `folder` is the `data.folder` key the vault added on 05.08.2026: a plain
 * string inside the encrypted body, trimmed, omitted when blank — the same
 * rule `buildData` applies in the New Item modal, and the same one Rust's
 * `derive_index` trims by. An empty-string folder would render as a nameless
 * sidebar group, so blank means ABSENT, never ''.
 *
 * Password managers all have folders under different names — LastPass calls it
 * `grouping`, Bitwarden CSV `folder`, Bitwarden JSON a `folderId` into a
 * `folders` array — and until 07.08.2026 every parser silently dropped them:
 * an import flattened a organised vault into one unfiled pile. The name is
 * kept VERBATIM (nested LastPass groups arrive as "Work\Sub"); inventing a
 * hierarchy mapping here would be a second folder model to maintain.
 */
export function withFolder(item, folder) {
  const f = str(folder).trim();
  if (f !== '') item.data = { ...item.data, folder: f };
  return item;
}

export function truthyFlag(v) {
  const s = str(v).trim().toLowerCase();
  return s === '1' || s === 'true' || s === 'yes' || s === 'y';
}

/** Build a password `data` object, keeping `password` even when empty. */
export function passwordData({ url, username, password, totp, notes }) {
  const d = { password: str(password) };
  const u = str(url).trim();
  const user = str(username).trim();
  const t = str(totp).trim();
  const n = str(notes).trim();
  if (u) d.url = u;
  if (user) d.username = user;
  if (t) d.totp = t;
  if (n) d.notes = n;
  return d;
}

/* ─────────────────────────────── CSV core ──────────────────────────────── */

/**
 * Robust CSV parse → array of rows (each row = array of cell strings). Handles
 * quoted fields, embedded commas/newlines, doubled-quote escapes, CRLF, and a
 * leading UTF-8 BOM. Never throws.
 */
export function parseCsv(text) {
  const rows = [];
  if (text == null) return rows;
  let s = String(text);
  if (s.charCodeAt(0) === 0xfeff) s = s.slice(1); // strip BOM

  let field = '';
  let row = [];
  let inQuotes = false;
  const n = s.length;
  let i = 0;
  const pushField = () => {
    row.push(field);
    field = '';
  };
  const pushRow = () => {
    rows.push(row);
    row = [];
  };

  while (i < n) {
    const c = s[i];
    if (inQuotes) {
      if (c === '"') {
        if (s[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      field += c;
      i += 1;
      continue;
    }
    if (c === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (c === ',') {
      pushField();
      i += 1;
      continue;
    }
    if (c === '\r') {
      pushField();
      pushRow();
      i += s[i + 1] === '\n' ? 2 : 1;
      continue;
    }
    if (c === '\n') {
      pushField();
      pushRow();
      i += 1;
      continue;
    }
    field += c;
    i += 1;
  }
  // Flush the last field/row when the input has no trailing newline.
  if (field !== '' || row.length > 0) {
    pushField();
    pushRow();
  }
  return rows;
}

/**
 * Quote a single CSV cell only when it contains a delimiter/quote/newline, and
 * neutralize spreadsheet formula injection (CWE-1236 / OWASP CSV Injection): a
 * cell whose first char is `= + - @` or a leading TAB/CR is treated as a
 * formula by Excel/Sheets on open. Prefix a single apostrophe so the value is
 * shown literally instead of evaluated. Applied to CSV output only (never to
 * JSON export or the parsers), which is the standard, round-trip-tolerant
 * mitigation.
 */
export function csvCell(v) {
  let s = str(v);
  if (s !== '' && /^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/** Stringify rows (array of arrays) to CRLF-delimited CSV. */
export function csvStringify(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) => (Array.isArray(row) ? row : []).map(csvCell).join(','))
    .join('\r\n');
}

/**
 * CSV → { header:[lowercased names], records:[{ colName: value }] }. Blank
 * lines are dropped; the first non-empty line is the header.
 */
export function parseCsvToObjects(text) {
  const rows = parseCsv(text).filter((r) => r.some((c) => str(c) !== ''));
  if (rows.length === 0) return { header: [], records: [] };
  const header = rows[0].map((h) => str(h).trim().toLowerCase());
  const records = [];
  for (let i = 1; i < rows.length; i += 1) {
    const r = rows[i];
    const obj = {};
    for (let j = 0; j < header.length; j += 1) obj[header[j]] = r[j] != null ? r[j] : '';
    records.push(obj);
  }
  return { header, records };
}
